import { useState } from 'react';
import { useMovements, useBillingAccount } from '../../lib/api/hooks-billing.js';
import { TableSkeleton, Empty, ErrorBanner, Badge } from '../../components/ui/Primitives.js';
import { formatMoney } from '../../lib/format.js';
import type { Movement } from '@arcnet/contracts';

/**
 * Every movement on the account's balance, newest first.
 *
 * Separate from Billing Information on purpose. That tab answers "what is this
 * customer's arrangement and where do they stand"; this one answers "what
 * happened, and when" -- which is the question asked during a dispute, and the
 * one that needs the whole list rather than a summary.
 */

/**
 * Money's SIGN is the thing a reader needs first.
 *
 * A charge and a top-up differ by which direction the balance moved, and a
 * column of unsigned numbers makes that invisible. Kinds are grouped by
 * direction rather than listed individually so a new movement kind inherits a
 * sensible presentation instead of rendering unstyled.
 */
const CREDIT_KINDS = new Set(['topup', 'refund', 'reversal']);

const KIND_LABEL: Record<Movement['kind'], string> = {
  call_charge: 'Call charge',
  topup:       'Top-up',
  adjustment:  'Adjustment',
  invoice:     'Invoice',
  refund:      'Refund',
  monthly_fee: 'Monthly fee',
  amd_check: 'AI answering-machine check',
  reversal:    'Reversal',
};

export function AccountTransactionsTab({ tenantId }: { tenantId: string }) {
  const movements = useMovements(tenantId);
  const billing = useBillingAccount(tenantId);
  const [kind, setKind] = useState<string>('all');

  if (movements.error) return <ErrorBanner error={movements.error} />;

  const all = movements.data?.data ?? [];
  const rows = kind === 'all' ? all : all.filter((m) => m.kind === kind);
  const currency = billing.data?.currency ?? 'ZAR';

  /**
   * The filter offers only kinds this account ACTUALLY has.
   *
   * A dropdown listing every possible movement kind mostly offers filters that
   * return nothing, which reads as a broken screen rather than an empty
   * category.
   */
  const present = [...new Set(all.map((m) => m.kind))];

  return (
    <>
      <div className="arc-toolbar" style={{ marginBottom: 12 }}>
        <select
          className="arc-select" style={{ width: 'auto' }}
          value={kind} onChange={(e) => setKind(e.target.value)}
          aria-label="Filter by transaction type"
        >
          <option value="all">All types ({all.length})</option>
          {present.map((k) => (
            <option key={k} value={k}>
              {KIND_LABEL[k] ?? k} ({all.filter((m) => m.kind === k).length})
            </option>
          ))}
        </select>
        <div className="arc-spacer" />
        <span className="arc-subtle arc-num" style={{ fontSize: 12.5 }}>
          {rows.length} shown
        </span>
      </div>

      <div className="arc-card">
        {movements.data === undefined ? (
          <TableSkeleton rows={8} cols={4} />
        ) : rows.length === 0 ? (
          <Empty
            icon="billing"
            title={all.length === 0 ? 'No transactions yet' : 'Nothing of that kind'}
            {...(all.length === 0 ? {
              hint: 'Charges appear here as calls are rated, alongside top-ups and adjustments.',
            } : {})}
          />
        ) : (
          <div className="arc-table-wrap">
            <table className="arc-table">
              <thead>
                <tr>
                  <th>Date</th>
                  <th>Type</th>
                  <th>Description</th>
                  <th style={{ textAlign: 'right' }}>Amount</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((m) => {
                  const credit = CREDIT_KINDS.has(m.kind);
                  return (
                    <tr key={m.id}>
                      <td className="arc-subtle" style={{ whiteSpace: 'nowrap' }}>
                        {new Date(m.created_at).toLocaleString('en-ZA', {
                          year: 'numeric', month: 'short', day: '2-digit',
                          hour: '2-digit', minute: '2-digit',
                        })}
                      </td>
                      <td>
                        {/* Only a CREDIT is worth marking; a call charge is
                            what every other row already is. */}
                        {credit
                          ? <Badge tone="ok">{KIND_LABEL[m.kind] ?? m.kind}</Badge>
                          : <span>{KIND_LABEL[m.kind] ?? m.kind}</span>}
                      </td>
                      <td className="arc-subtle">{m.description ?? '—'}</td>
                      <td
                        className="arc-num"
                        style={{
                          whiteSpace: 'nowrap',
                          textAlign: 'right',
                          color: credit ? 'var(--arc-ok)' : undefined,
                        }}
                      >
                        {credit ? '+' : ''}{formatMoney(m.amount, m.currency ?? currency)}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </>
  );
}
